import { equipmentStatusLabel } from "../../domain/shared/status-labels.js";
import { createIcons, Plus, FileText, Download, Sparkles } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";
import "../components/document/pdf-preview.js";

class AppEquipmentPage extends HTMLElement {
  connectedCallback() {
    this.loadData();
  }

  private async loadData() {
    this.innerHTML = `<div style="padding: 40px; color: var(--color-text-muted);">Carregando equipamentos...</div>`;
    
    try {
      const hasCreatePermission = contextStore.hasPermission(PermissionCode.EquipmentCreate);
      const equipments = await window.app.platform.equipmentService.listEquipments(sessionRequestContext());
      this.renderSuccess(equipments, hasCreatePermission);
    } catch (e: any) {
      this.innerHTML = `<div style="padding: 40px; color: var(--ia-danger-fg, #e53e3e);">Erro ao carregar equipamentos: ${e.message}</div>`;
    }
  }

  private renderSuccess(equipments: readonly any[], hasCreatePermission: boolean) {
    this.innerHTML = `
        <style>
          .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
          }
          .page-title {
            font-size: 1.5rem;
            font-weight: 500;
            color: var(--color-text-primary);
          }
          .btn-primary {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            background-color: var(--ia-action-bg, var(--color-primary));
            color: var(--ia-action-fg, var(--color-text-primary));
            border: none;
            padding: 0.5rem 1rem;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 500;
            transition: filter 0.2s;
          }
          .btn-primary:hover {
            filter: brightness(1.1);
          }
          .equipment-card {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .equipment-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          .equipment-actions {
            display: flex;
            gap: 8px;
          }
          .doc-modal {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.8);
            z-index: 1000;
            display: none;
            padding: 40px;
          }
          .doc-modal.active {
            display: flex;
            flex-direction: column;
          }
          .doc-modal-content {
            flex: 1;
            background: var(--color-background);
            border-radius: 8px;
            overflow: hidden;
            position: relative;
          }
          .doc-modal-close {
            position: absolute;
            top: 16px; right: 16px;
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            color: var(--color-text-primary);
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
            z-index: 10;
          }
        </style>
        
        <div class="page-header">
          <div class="page-title">Equipamentos</div>
          ${hasCreatePermission ? `
            <button class="btn-primary" id="btn-new-equipment">
              <i data-lucide="plus" style="width: 18px; height: 18px;"></i> Novo Equipamento
            </button>
          ` : ''}
        </div>

        <div id="equipment-list">
          ${equipments.length === 0 ? `
            <div style="padding: 40px; border: 1px dashed var(--color-border); border-radius: 8px; text-align: center; color: var(--color-text-muted);">
              <i data-lucide="package" style="width: 48px; height: 48px; ; margin-bottom: 16px;"></i>
              <p>Nenhum equipamento cadastrado.</p>
            </div>
          ` : equipments.map(eq => `
            <div class="equipment-card">
              <div class="equipment-info">
                <strong style="color: var(--color-text-primary);">${eq.tag} - ${eq.name}</strong>
                <span style="color: var(--color-text-muted); font-size: 0.875rem;">Status: ${equipmentStatusLabel(eq.status)} | Serviço: ${eq.service || 'N/A'}</span>
              </div>
              <div class="equipment-actions">
                <app-button variant="secondary" size="sm" class="btn-doc" data-type="AVR" data-eq-id="${eq.id}">
                  <span style="display: flex; align-items: center; gap: 4px;">
                    <i data-lucide="file-text" style="width: 14px; height: 14px;"></i> Gerar AVR
                  </span>
                </app-button>
                <app-button variant="secondary" size="sm" class="btn-ai-assistant" data-eq-id="${eq.id}">
                  <span style="display: flex; align-items: center; gap: 4px; color: var(--ia-primary);">
                    <i data-lucide="sparkles" style="width: 14px; height: 14px;"></i> Assistente Técnico
                  </span>
                </app-button>
              </div>
            </div>
          `).join('')}
        </div>

        <div class="doc-modal" id="doc-modal" style="padding: 24px; box-sizing: border-box;">
          <div class="doc-modal-content" style="display: flex; flex-direction: column;">
            <div style="display: flex; justify-content: space-between; align-items: center; background: var(--color-graphite-800); padding: 10px 16px; border-bottom: 1px solid var(--color-graphite-700);">
              <span style="color: var(--color-text-primary); font-weight: 600; font-size: 0.95rem;">Visualização do Laudo AVR de Equipamento</span>
              <button class="doc-modal-close" id="btn-close-modal" style="background: var(--status-danger-solid); border: none; color: var(--color-text-primary); padding: 6px 14px; border-radius: 4px; font-weight: 600; font-size: 0.85rem; cursor: pointer; display: flex; align-items: center; gap: 6px;">
                ✕ Fechar Janela (ESC)
              </button>
            </div>
            <app-pdf-preview id="pdf-preview" style="flex: 1;"></app-pdf-preview>
          </div>
        </div>
        
        <ai-assistant-drawer id="ai-assistant"></ai-assistant-drawer>
      `;

      createIcons({
        icons: { Plus, FileText, Download, Sparkles },
        nameAttr: "data-lucide",
        root: this
      });

      const modal = this.querySelector('#doc-modal') as HTMLElement;
      const btnClose = this.querySelector('#btn-close-modal');
      const pdfPreview = this.querySelector('#pdf-preview') as any;
      const btnNewEquipment = this.querySelector('#btn-new-equipment');

      btnNewEquipment?.addEventListener('click', () => {
        router.navigate('/equipment/new');
      });

    const closeModal = () => {
      modal.classList.remove('active');
    };

    btnClose?.addEventListener('click', closeModal);

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    pdfPreview?.addEventListener('close-preview', closeModal);
    pdfPreview?.addEventListener('close', closeModal);

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        closeModal();
      }
    });

    const docButtons = this.querySelectorAll('.btn-doc');
    docButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const type = (e.currentTarget as HTMLElement).getAttribute('data-type');
        const eqId = (e.currentTarget as HTMLElement).getAttribute('data-eq-id');
        const eq = equipments.find(item => item.id === eqId || item.tag === eqId) || (equipments.length > 0 ? equipments[0] : null);
        
        if (!eq) {
          alert('Selecione ou cadastre um equipamento válido.');
          return;
        }

        const snapshot = {
          id: `AVR-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
          type: type,
          revision: '00',
          createdAt: new Date(),
          data: {
            client: contextStore.get().activeClientName || 'Cliente',
            avrNumber: `AVR-${new Date().getFullYear()}-0001`,
            revision: '00',
            tag: eq.tag,
            type: eq.equipmentType || 'EQUIPAMENTO',
            date: new Date().toLocaleDateString('pt-BR'),
            area: eq.area || '-',
            os: '-',
            subOs: '-',
            material: eq.material || '-',
            fluid: eq.service || '-',
            summary: {
              total: '0', baixa: '0', moderada: '0', alta: '0', critica: '0', semDados: '0'
            },
            components: [],
            analysis: 'Análise de vida remanescente calculada a partir das medições de espessura.',
            conclusion: {
              status: 'EM ANÁLISE',
              worstCondition: 'REGULAR',
              observation: 'Laudo gerado pelo sistema.'
            },
            signatories: [
              { name: contextStore.get().userName, role: 'INSPETOR', cred: 'Inspetor da Conta', date: new Date().toLocaleDateString('pt-BR') },
              { name: 'Eng. Roberto Almeida', role: 'ENGENHEIRO PLH', cred: 'CREA: 506123456-SP', date: new Date().toLocaleDateString('pt-BR') }
            ]
          }
        };

        pdfPreview.snapshot = snapshot;
        modal.classList.add('active');
      });
    });

    // ASSISTENTE TÉCNICO (INSPECT IA REVIEW): responde pela Base Técnica, citando a fonte.
    // Não há mais "explicação" montada com valores inventados (taxa 0,00 mm/ano fixa).
    const assistant = this.querySelector('#ai-assistant') as (HTMLElement & { open?: (opts: { title?: string }) => void }) | null;
    this.querySelectorAll('.btn-ai-assistant').forEach(btn => btn.addEventListener('click', (e) => {
      const eqId = (e.currentTarget as HTMLElement).getAttribute('data-eq-id');
      const eq = equipments.find(item => item.id === eqId || item.tag === eqId) || null;
      assistant?.open?.({ title: eq ? `Ativo ${eq.tag}` : 'Assistente Técnico' });
    }));
  }
}

if (!customElements.get("app-equipment-page")) {
  customElements.define("app-equipment-page", AppEquipmentPage);
}


